import { useState, useCallback } from 'react'

const ENHANCE_API_URL = 'https://enhance.pixelmaker.ai/enhance'

interface UseAutoPromptReturn {
  isEnhancing: boolean
  error: boolean
  enhance: (imageUrl: string) => Promise<string | null>
  clearError: () => void
}

export function useAutoPrompt(): UseAutoPromptReturn {
  const [isEnhancing, setIsEnhancing] = useState(false)
  const [error, setError] = useState(false)

  const enhance = useCallback(async (imageUrl: string): Promise<string | null> => {
    if (!imageUrl || isEnhancing) return null

    setIsEnhancing(true)
    setError(false)
    
    try {
      const response = await fetch(ENHANCE_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ image_url: imageUrl }),
      })

      if (!response.ok) {
        throw new Error(`Enhancement failed: ${response.statusText}`)
      }

      const data = await response.json()

      if (data.status === 'success' && data.enhanced_text) {
        return data.enhanced_text
      }
      
      throw new Error(data.message || 'No prompt returned')
    } catch (err) {
      console.error('Enhancement error:', err)
      setError(true)
      return null
    } finally {
      setIsEnhancing(false)
    }
  }, [isEnhancing])

  return {
    isEnhancing,
    error,
    enhance,
    clearError: () => setError(false),
  }
}
